import { inject, provide } from 'vue';

import i18n from '@/i18n';
import { MissionCommand } from '@/types/mission';

import {
	type DropdownGeneratorAction,
	DropdownGeneratorActionType,
	DropdownItemColors,
} from '@/dsComponents/Dropdown/types';

import {
	agencyMissionActionsList,
	workerMissionActionsList,
	workerMissionActionsSymbol,
} from './constant';

export function useWorkerMissionActions() {
	const workerMissionActionsProvider = (
		fn: (commandName: MissionCommand) => void
	) => provide(workerMissionActionsSymbol, fn);

	const handleActionToParent = inject(workerMissionActionsSymbol);

	const workerActions = (
		workerAvailableCommands: MissionCommand[]
	): DropdownGeneratorAction[] => {
		const agencyMissionActions = agencyMissionActionsList();
		const workerMissionActions = workerMissionActionsList();
		const actions: (DropdownGeneratorAction & {
			commandName?: MissionCommand;
		})[] = [];

		const commandsForAll = [MissionCommand.propose, MissionCommand.repropose];

		if (
			!workerAvailableCommands.every((missionCommand) =>
				commandsForAll.includes(missionCommand)
			)
		)
			actions.push(
				{
					type: DropdownGeneratorActionType.item,
					commandName: MissionCommand.workerDetails,
					item: {
						iconLeft: 'file-search',
						iconLeftColor: DropdownItemColors.lightGrey,
						labelColor: DropdownItemColors.lightGrey,
						label: i18n.global.t(
							'mission.availableCommands.workerDetails.text'
						),
					},
				},
				{
					type: DropdownGeneratorActionType.separator,
				}
			);

		const agencyMissionActionsFiltered = agencyMissionActions.filter((action) =>
			isActionAvailable(action, workerAvailableCommands)
		);

		const workerMissionActionsFiltered = workerMissionActions
			.filter((action) => isActionAvailable(action, workerAvailableCommands))
			.filter((ms, index, arr) => {
				if (ms.type !== 'item' && !arr[index + 1]) {
					return false;
				}
				return true;
			});

		actions.push(...agencyMissionActionsFiltered);

		if (workerMissionActionsFiltered.length > 0) {
			const separator: DropdownGeneratorAction = {
				type: DropdownGeneratorActionType.separator,
			};

			actions.push(separator, ...workerMissionActionsFiltered);
		}

		return actions.map((action) => ({
			...action,
			item:
				action.type === 'item'
					? {
							...action.item,
							onClick: () => {
								if (handleActionToParent && action.commandName) {
									handleActionToParent(action.commandName);
								}
							},
						}
					: () => {},
		}));
	};

	return {
		handleActionToParent,
		workerActions,
		workerMissionActionsProvider,
	};
}

function isActionAvailable(
	action: DropdownGeneratorAction & { commandName?: MissionCommand },
	availableCommands: MissionCommand[]
): boolean {
	if (action.type !== 'item') {
		return true;
	}
	return availableCommands.includes(
		action.commandName ?? ('' as MissionCommand)
	);
}
